import { useEffect, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Card,
    CardActions,
    CardContent,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    Link,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';

import AccountBalance from '@mui/icons-material/AccountBalance';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import FlagIcon from '@mui/icons-material/Flag';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import ReorderIcon from '@mui/icons-material/Reorder';
import SouthAmericaIcon from '@mui/icons-material/SouthAmerica';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TopicIcon from '@mui/icons-material/Topic';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';

export const ResultType = {
    All: 'all',
    Region: 'region',
    Country: 'country',
    State: 'state',
    Port: 'port',
    MOU: 'mou',
    Incentive: 'incentive',
    AltFuel: 'altFuel'
};

const SortType = {
    Region: 'region',
    Port: 'port',
    MOU: 'mou'
};

const RegulationInfo = ({ expanded, key, regulation }) => {
    const [lowestGeographyName, setLowestGeographyName] = useState();
    const [icon, setIcon] = useState();
    const [subjectIcon, setSubjectIcon] = useState();
    const [expand, setExpand] = useState(expanded);

    useEffect(() => {
        if (regulation.port.length > 0) {
            setLowestGeographyName(regulation.port);
            setIcon(<DirectionsBoatIcon sx={{ height: '24px', margin: '0 2px 0 0' }} />);
        } else if (regulation.state.length > 0) {
            setLowestGeographyName(regulation.state);
            setIcon(<AccountBalanceIcon sx={{ height: '24px', margin: '0 2px 0 0' }} />);
        } else if (regulation.country.length > 0) {
            setLowestGeographyName(regulation.country);
            setIcon(<FlagIcon sx={{ height: '24px', margin: '0 2px 0 0' }} />);
        } else if (regulation.region.length > 0) {
            setLowestGeographyName(regulation.region);
            setIcon(<SouthAmericaIcon sx={{ height: '24px', margin: '0 2px 0 0' }} />);
        } else if (regulation.campaign.length > 0) {
            setLowestGeographyName(`All countries in the ${regulation.campaign}`);
            setIcon(<FlagIcon sx={{ height: '24px', margin: '0 2px 0 0' }} />);
        }

        if (regulation.subject === 'Ports Incentives') {
            setSubjectIcon(<AttachMoneyIcon sx={{ height: '24px', margin: '0 0px 0 8px' }} />);
        } else {
            setSubjectIcon(<TopicIcon sx={{ height: '24px', margin: '0 2px 0 12px' }} />);
        }
    }, [regulation, key, expanded]);

    return (
        <Paper elevation={3} sx={{ marginBottom: '32px', width: { xs: '340px', sm: '100%' }, marginLeft: { xs: '12px', sm: '0' } }}>
            <Card>
                <CardContent>
                    <Stack>
                        <Stack direction="row">
                            {icon}
                            <Typography variant="caption" component="div" sx={{ fontSize: '14px' }}>
                                {lowestGeographyName}
                            </Typography>
                            {subjectIcon}
                            <Typography variant="caption" component="div" sx={{ fontSize: '14px' }}>
                                {regulation.subject}
                            </Typography>
                            {regulation.campaign.length > 0 && (
                                <>
                                    <LocalPoliceIcon color="red" sx={{ height: '24px', margin: '0 2px 0 4px' }} />
                                    <Typography variant="caption" component="div" sx={{ fontSize: '14px' }}>
                                        {regulation.campaign} inspection campaign
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontStyle: 'italic', fontSize: '11px', margin: '4px 0 0 6px' }}>{`${regulation.start_date}-${regulation.end_date}`}</Typography>
                                </>
                            )}
                        </Stack>
                        <Typography variant="h6" component="div" sx={{ padding: '10px 0', lineHeight: '1.2' }}>
                            {regulation.name}{' '}
                        </Typography>
                        <CardActions sx={{ padding: '8px 0' }}>
                            <Link href={regulation.link_url} underline="always" target="_blank">
                                {regulation.link_display}
                            </Link>
                        </CardActions>
                    </Stack>
                    <Accordion
                        disableGutters={true}
                        sx={{ backgroundColor: '#FFFFFF', marginTop: '24px', boxShadow: 'none' }}
                        expanded={expanded || expand}
                        onChange={(event, exp) => {
                            setExpand(exp);
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                            {regulation.special_area.length > 0 && (
                                <Stack direction="row" sx={{ marginTop: '8px' }}>
                                    <InfoIcon color="red" sx={{ height: '16px', marginTop: '2px' }} />
                                    <Typography variant="caption" component="div" sx={{ fontSize: '14px' }}>
                                        Application guidance only applies to {regulation.special_area}
                                    </Typography>
                                </Stack>
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body1" dangerouslySetInnerHTML={{ __html: regulation.description }} />
                        </AccordionDetails>
                    </Accordion>
                </CardContent>
            </Card>
        </Paper>
    );
};

const RegulationItem = ({ regulationData, resultTypeSelected, onResultTypeChange, sortTypeSelected, onSortTypeChange, subjectList, subjectSelected, onSubjectChange }) => {
    // Pagination
    const [currentPageData, setCurrentPageData] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageRowOption, setPageRowOption] = useState(5);

    // Filters
    const [expandAll, setExpandAll] = useState(false);

    useEffect(() => {
        setPageData(1, pageRowOption);
        setPageNumber(1);
    }, [regulationData]);

    const handleExpandAllChange = () => {
        setExpandAll(!expandAll);
    };

    const handlePageChange = (event, pageNumber) => {
        setPageData(pageNumber, pageRowOption);
        setPageNumber(pageNumber);
    };

    const setPageData = (pageNumber, rowsPerPage) => {
        const startIndex = (pageNumber - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;

        const tempPageData = regulationData.slice(startIndex, endIndex);

        setCurrentPageData(tempPageData);
    };

    return (
        <div>
            <Box sx={{ margin: { xs: '24px 0', sm: '24px 0', md: '32px 128px' } }}>
                <Stack direction={'row'} spacing={2} sx={{ marginBottom: '24px', justifyContent: 'flex-start' }}>
                    {/* Result Type (Region, Country, State, Port, MOU, and Port Incentives) */}
                    <FormControl sx={{ minWidth: 120, display: { xs: 'none', sm: 'none', md: 'flex' } }} size="small">
                        <InputLabel>Showing</InputLabel>
                        <Select value={resultTypeSelected} onChange={onResultTypeChange} label="Showing">
                            <MenuItem value={ResultType.All}>
                                <ReorderIcon sx={{ height: '18px' }} />
                                All Items
                            </MenuItem>
                            <MenuItem value={ResultType.Region}>
                                <SouthAmericaIcon sx={{ height: '18px' }} />
                                Region Requirements
                            </MenuItem>
                            <MenuItem value={ResultType.Country}>
                                <FlagIcon sx={{ height: '18px' }} />
                                Country Requirements
                            </MenuItem>
                            <MenuItem value={ResultType.State}>
                                <AccountBalance sx={{ height: '18px' }} />
                                State Requirements
                            </MenuItem>
                            <MenuItem value={ResultType.Port}>
                                <DirectionsBoatIcon sx={{ height: '18px' }} />
                                Port Requirements
                            </MenuItem>
                            <MenuItem value={ResultType.Incentive}>
                                <AttachMoneyIcon sx={{ height: '18px' }} />
                                Port Incentives
                            </MenuItem>
                            <MenuItem value={ResultType.AltFuel}>
                                <EnergySavingsLeafIcon sx={{ height: '18px' }} />
                                Alt. Fuels
                            </MenuItem>
                        </Select>
                    </FormControl>
                    {/* Order By */}
                    {resultTypeSelected === ResultType.All && (
                        <FormControl sx={{ minWidth: 120, display: { xs: 'none', sm: 'none', md: 'flex' } }} size="small">
                            <InputLabel>Order by</InputLabel>

                            <Select value={sortTypeSelected} label="Order by" onChange={onSortTypeChange}>
                                <MenuItem value={SortType.Region}>Region &gt; Port</MenuItem>
                                <MenuItem value={SortType.Port}>Port &gt; Region</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                    {/* Subject */}
                    {subjectList.length > 0 && (
                        <Autocomplete
                            sx={{ minWidth: 180, display: { xs: 'none', sm: 'none', md: 'flex' } }}
                            size="small"
                            id="subjects-autocomplete"
                            options={subjectList}
                            value={subjectSelected}
                            onChange={onSubjectChange}
                            getOptionLabel={(option) => option.name}
                            renderOption={(props, option) => (
                                <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                    {option.name}
                                </Box>
                            )}
                            renderInput={(params) => <TextField {...params} label="Filter by subject" placeholder="Select subject" sx={{ width: 410 }} />}
                        />
                    )}

                    {/* Expand All */}
                    {currentPageData.length > 0 && (
                        <FormGroup key="1" sx={{ display: { xs: 'none', sm: 'none', md: 'flex' }, flexDirection: 'row' }}>
                            <FormControlLabel control={<Switch checked={expandAll} onChange={handleExpandAllChange} />} label="Expand All" />
                        </FormGroup>
                    )}
                </Stack>

                <Stack direction={'column'} spacing={2}>
                    {/* No Regulations To Show */}
                    {(!currentPageData || currentPageData.length <= 0) && (
                        <Typography variant="body1" component="div" sx={{ margin: '0' }}>
                            No specific guidance. Please change your filter criteria.
                        </Typography>
                    )}

                    {currentPageData && currentPageData.length > 0 && (
                        <>
                            {/* Top Pagination */}
                            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                                <div></div>
                                <Pagination
                                    color="primary"
                                    count={Math.ceil(parseFloat(regulationData.length / pageRowOption))}
                                    page={pageNumber}
                                    onChange={handlePageChange}
                                    rowsPerPage={pageRowOption}
                                    rowsPerPageOptions={[5]}
                                    variant="outlined"
                                    shape="rounded"
                                />
                                <Typography variant="h6">No. Items : {`${regulationData.length}`}</Typography>
                            </Stack>

                            {/* Regulation Card Information */}
                            {currentPageData.map((regulation, index) => (
                                <RegulationInfo expanded={expandAll} key={index} regulation={regulation} />
                            ))}

                            {/* Bottom Pagination */}
                            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                                <div></div>
                                <Pagination
                                    color="primary"
                                    count={Math.ceil(parseFloat(regulationData.length / pageRowOption))}
                                    page={pageNumber}
                                    onChange={handlePageChange}
                                    rowsPerPage={pageRowOption}
                                    rowsPerPageOptions={[5]}
                                    variant="outlined"
                                    shape="rounded"
                                />
                                <Typography variant="h6">No. Items : {`${regulationData.length}`}</Typography>
                            </Stack>
                        </>
                    )}
                </Stack>
            </Box>
        </div>
    );
};

export default RegulationItem;
